import { Link } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { FESTS } from "../../data/fests";
import { kindLabel, overnightLabel } from "../../lib/access";
import {
  cadenceLabel,
  formatRange,
  festsInWindow,
  groupByMonth,
  WINDOW_DAYS,
  windowEnd,
} from "../../lib/season";

const Calendar = () => {
  const rows = festsInWindow(FESTS);
  const happening = rows.filter((row) => row.status === "now");
  const upcoming = rows.filter((row) => row.status === "upcoming");
  const months = groupByMonth(rows);
  const through = windowEnd();
  const campingNow = happening.filter((row) => row.fest.overnight !== "day-trip").length;

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 py-12 w-full">
        <p className="text-sun font-semibold uppercase tracking-wide text-sm">
          Next {WINDOW_DAYS} days
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-alt-blk mt-3">
          Upcoming seasons
        </h1>
        <p className="text-xl text-alt-blk/80 mt-4 max-w-3xl leading-relaxed">
          Through{" "}
          {through.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })}
          . Happening now first, then the next gates. Dates are a planning
          window — still buy the ticket on the official page.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Happening now
            </div>
            <div className="text-2xl font-bold mt-1">{happening.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Opening in this window
            </div>
            <div className="text-2xl font-bold mt-1">{upcoming.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Campable now
            </div>
            <div className="text-2xl font-bold mt-1">{campingNow}</div>
          </div>
        </div>

        {happening.length > 0 && (
          <>
            <h2 className="text-2xl font-bold mt-12 mb-4">Gates are open</h2>
            <ul className="space-y-3">
              {happening.map(({ fest, season }) => (
                <li
                  key={`${fest.id}-${season.start}`}
                  className="rounded-2xl bg-alt-blk text-alt-wht border border-alt-blk p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                >
                  <div>
                    <Link
                      to={`/fests/${fest.id}`}
                      className="font-bold text-lg text-ray hover:text-sunset"
                    >
                      {fest.name}
                    </Link>
                    <p className="text-sm text-alt-wht/70 mt-1">
                      {fest.city}, {fest.state} · {kindLabel(fest)}
                    </p>
                    <p className="text-sm text-alt-wht/80 mt-2 line-clamp-2">
                      {fest.whyGo}
                    </p>
                  </div>
                  <div className="text-sm sm:text-right shrink-0">
                    <div className="font-semibold">
                      {formatRange(season.start, season.end)}
                    </div>
                    <div className="text-foam">
                      {cadenceLabel(season.cadence)} · {overnightLabel(fest.overnight)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <h2 className="text-2xl font-bold mt-12 mb-4">By month</h2>
        {months.map((month) => (
          <div key={month.key} className="mt-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-alt-blk/50 mb-3">
              {month.label}
            </h3>
            <ul className="space-y-3">
              {month.rows.map(({ fest, season, status }) => (
                <li
                  key={`${fest.id}-${season.start}`}
                  className="rounded-2xl bg-white border border-alt-blk/10 p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/fests/${fest.id}`}
                        className="font-bold text-lg hover:text-mars"
                      >
                        {fest.name}
                      </Link>
                      {status === "now" && (
                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-mars text-white">
                          Now
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-alt-blk/70 mt-1">
                      {fest.city}, {fest.state} · {kindLabel(fest)}
                    </p>
                  </div>
                  <div className="text-sm sm:text-right shrink-0">
                    <div className="font-semibold">
                      {formatRange(season.start, season.end)}
                    </div>
                    <div className="text-turquoise">
                      {cadenceLabel(season.cadence)} · {overnightLabel(fest.overnight)}
                    </div>
                    <Link
                      to={`/?fest=${fest.id}`}
                      className="inline-block mt-2 text-turquoise hover:text-mars"
                    >
                      Map
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <p className="text-sm text-alt-blk/60 mt-8">
          Typical seasons fill in when a dated run has closed, so next year’s
          window still shows.{" "}
          <Link to="/" className="text-turquoise hover:text-mars">
            Pick a fest on the map
          </Link>{" "}
          while the season is open.
        </p>
      </section>
    </Layout>
  );
};

export default Calendar;
